/**
 * nativePurchaseService.js
 * 
 * Handles in-app purchases for iOS (StoreKit 2) and Android (Google Play Billing)
 * Uses @capgo/native-purchases Capacitor plugin
 * 
 * Product IDs (must match App Store Connect & Google Play Console):
 *   - echomemoir.storyteller.monthly  (base plan: storyteller-monthly)
 *   - echomemoir.vault.monthly        (base plan: vault-monthly)
 */

import { Capacitor } from '@capacitor/core';

// Static import — dynamic import fails with Capacitor 8 + Swift Package Manager
let NativePurchasesPlugin = null;
let PURCHASE_TYPE_ENUM = null;
try {
  const mod = require('@capgo/native-purchases');
  NativePurchasesPlugin = mod.NativePurchases;
  PURCHASE_TYPE_ENUM = mod.PURCHASE_TYPE;
} catch (e) {
  console.warn('native-purchases not available:', e.message);
}

// Product configuration
const PRODUCTS = {
  legacy_keeper: {
    productId: 'echomemoir.storyteller.monthly',
    planId: 'storyteller-monthly', // Google Play base plan ID
    tier: 'legacy_keeper',
  },
  legacy_vault: {
    productId: 'echomemoir.vault.monthly',
    planId: 'vault-monthly', // Google Play base plan ID
    tier: 'legacy_vault',
  },
};

class NativePurchaseService {
  constructor() {
    this.initialized = false;
    this.products = [];
    this.NativePurchases = null;
    this.PURCHASE_TYPE = null;
  }

  /**
   * Check if we're running on a native platform (iOS/Android)
   */
  isNative() {
    return Capacitor.isNativePlatform();
  }

  /**
   * Get the current platform
   */
  getPlatform() {
    return Capacitor.getPlatform(); // 'ios', 'android', or 'web'
  }

  /**
   * Initialize the billing service
   * Call this early in app lifecycle (e.g., App.js or after login)
   */
  async initialize() {
    if (!this.isNative() || this.initialized) return;

    try {
      // Use statically imported module (dynamic import fails with Capacitor 8 + SPM)
      if (!NativePurchasesPlugin || !PURCHASE_TYPE_ENUM) {
        console.warn('IAP: @capgo/native-purchases module not available');
        return;
      }
      this.NativePurchases = NativePurchasesPlugin;
      this.PURCHASE_TYPE = PURCHASE_TYPE_ENUM;

      // Check billing support
      const { isBillingSupported } = await this.NativePurchases.isBillingSupported();
      if (!isBillingSupported) {
        console.warn('Billing not supported on this device');
        return;
      }

      // Load subscription products
      const productIds = Object.values(PRODUCTS).map(p => p.productId);
      const { products } = await this.NativePurchases.getProducts({
        productIdentifiers: productIds,
        productType: this.PURCHASE_TYPE.SUBS,
      });

      this.products = products;
      this.initialized = true;
      console.log('Native purchases initialized. Products:', products.map(p => `${p.title} - ${p.priceString}`));
    } catch (error) {
      console.error('Failed to initialize native purchases:', error);
    }
  }

  /**
   * Get product details (price, title, etc.) for a tier
   * Returns null if not available
   */
  getProductForTier(tier) {
    const config = PRODUCTS[tier];
    if (!config) return null;
    return this.products.find(p => p.productIdentifier === config.productId) || null;
  }

  /**
   * Get formatted price string for a tier (e.g., "$9.99")
   * Falls back to hardcoded price if native not available
   */
  getPriceString(tier) {
    const product = this.getProductForTier(tier);
    if (product) return product.priceString;

    // Fallback
    if (tier === 'legacy_keeper') return '$9.99';
    if (tier === 'legacy_vault') return '$29.99';
    return '$0';
  }

  /**
   * Purchase a subscription
   * @param {string} tier - 'legacy_keeper' or 'legacy_vault'
   * @param {string} userId - The user's ID for linking purchase to account
   * @returns {object} Transaction result with purchaseToken
   */
  async purchase(tier, userId) {
    if (!this.initialized || !this.NativePurchases) {
      throw new Error('Native purchases not initialized');
    }

    const config = PRODUCTS[tier];
    if (!config) {
      throw new Error(`Invalid tier: ${tier}`);
    }

    try {
      const transaction = await this.NativePurchases.purchaseProduct({
        productIdentifier: config.productId,
        planIdentifier: config.planId, // Required for Android subscriptions
        productType: this.PURCHASE_TYPE.SUBS,
        quantity: 1,
      });

      console.log('Purchase successful:', transaction);

      return {
        transactionId: transaction.transactionId,
        productId: transaction.productIdentifier || config.productId,
        purchaseToken: transaction.purchaseToken, // Android
        receipt: transaction.receipt, // iOS
        jwsRepresentation: transaction.jwsRepresentation, // iOS StoreKit 2
        platform: this.getPlatform(),
        tier: config.tier,
      };
    } catch (error) {
      // User cancelled
      if (error.message?.includes('cancel') || error.code === 'USER_CANCELLED') {
        throw new Error('CANCELLED');
      }
      throw error;
    }
  }

  /**
   * Restore previous purchases (e.g., after reinstall)
   */
  async restorePurchases() {
    if (!this.initialized || !this.NativePurchases) {
      throw new Error('Native purchases not initialized');
    }

    try {
      const { purchases } = await this.NativePurchases.getPurchases({
        productType: this.PURCHASE_TYPE.SUBS,
      });
      return purchases;
    } catch (error) {
      console.error('Restore purchases failed:', error);
      throw error;
    }
  }

  /**
   * Open native subscription management page
   */
  async manageSubscriptions() {
    if (!this.initialized || !this.NativePurchases) {
      throw new Error('Native purchases not initialized');
    }

    try {
      await this.NativePurchases.manageSubscriptions();
    } catch (error) {
      console.error('Manage subscriptions failed:', error);
      // Fallback: open URL
      const platform = this.getPlatform();
      if (platform === 'android') {
        window.open('https://play.google.com/store/account/subscriptions', '_blank');
      } else if (platform === 'ios') {
        window.open('https://apps.apple.com/account/subscriptions', '_blank');
      }
    }
  }
}

// Singleton
const nativePurchaseService = new NativePurchaseService();
export default nativePurchaseService;